import { pick } from 'es-toolkit';

/** RestClient services use idField; id_field is legacy. */
const idFieldName = (service) => service.idField ?? service.id_field;

/** Optional namespace disambiguates reducers that share the same resource keys (e.g. PDF vs email layouts). */
const actionType = (actionNamespace, segment) => (
    actionNamespace ? `kronos/${actionNamespace}/${segment}` : `kronos/${segment}`
);

const initialState = {
    isLoading: false,
    listFetchScope: 'idle',
    isLoadingLinkedResources: false,
    hasLoadingError: false,
    loadingErrorMessage: '',
    isLoadingOne: false,
    hasLoadingOneError: false,
    loadingOneErrorMessage: '',
    isDeleting: false,
    hasDeleteError: false,
    deleteErrorMessage: '',
    hasFinishedDeleting: false,
};

const resetSlice = (service, actionNamespace) => {
    const property     = service.resource;
    const listProperty = service.resources;

    const RESET = actionType(actionNamespace, `${property}/RESET`);

    const resetReducer = (state, action) => {
        switch (action.type) {
            case RESET:
                return {
                    ...initialState,
                    [property]: null,
                    [listProperty]: null,
                    [`${property}SuccessfullyCreated`]: false,
                    [`${property}SuccessfullyDeleted`]: false,
                }
            default:
                return state;
        }
    };

    const resetAction = () => { return { type: RESET } };

    return { resetReducer, resetAction };
}

const startCreateSlice = (service, actionNamespace) => {
    const property = service.resource;

    const START_CREATING_ITEM = actionType(actionNamespace, `${property}/START_CREATING_ITEM`);

    const startCreateReducer = (state, action) => {
        switch (action.type) {
            case START_CREATING_ITEM:
                return {
                    ...state,
                    [property]: null,
                    isLoading: false,
                    isLoadingOne: false,
                    hasLoadingError: false,
                    hasLoadingOneError: false,
                    [`${property}SuccessfullyCreated`]: false,
                    [`${property}SuccessfullyDeleted`]: false,
                }
            default:
                return state;
        }
    };

    const startCreateAction = () => { return { type: START_CREATING_ITEM } };

    return { startCreateReducer, startCreateAction };
}

const createSlice = (service, actionNamespace) => {
    const property     = service.resource;
    const listProperty = service.resources;

    const CREATING_ITEM         = actionType(actionNamespace, `${property}/CREATING_ITEM`);
    const CREATING_ITEM_SUCCESS = actionType(actionNamespace, `${property}/CREATING_ITEM_SUCCESS`);
    const CREATING_ITEM_FAILURE = actionType(actionNamespace, `${property}/CREATING_ITEM_FAILURE`);

    const createReducer = (state, action) => {
        switch (action.type) {
            case CREATING_ITEM:
                return {
                    ...state,
                    isLoadingOne: true,
                    hasLoadingError: false,
                    loadingErrorMessage: ''
                };
            case CREATING_ITEM_SUCCESS:
                return {
                    ...state,
                    isLoadingOne: false,
                    hasLoadingOneError: false,
                    [`${property}SuccessfullyCreated`]: true,
                    [property]: action[property],
                    [listProperty]: [...(state[listProperty] || []), action[property]]
                };
            case CREATING_ITEM_FAILURE:
                return {
                    ...state,
                    isLoadingOne: false,
                    hasLoadingOneError: true,
                    [`${property}SuccessfullyCreated`]: false,
                    loadingErrorMessage: action.error.message
                };
            default:
                return state;
        }
    }

    function creatingItem() { return { type: CREATING_ITEM } }
    function creatingItemSuccess(item) { return { type: CREATING_ITEM_SUCCESS, [property]: item } }
    function creatingItemFailure(err) { return { type: CREATING_ITEM_FAILURE, error: err } }
    const createAction = (item, params, callback) => {
        return (dispatch) => {
            dispatch(creatingItem());
            return service.create(item, params)
                .then(data => {
                    const item = data[property];
                    dispatch(creatingItemSuccess(item));
                    callback && callback(/*err*/null, item)
                })
                .catch(err => {
                    dispatch(creatingItemFailure(err))
                    callback && callback(err);
                });
        }
    };

    return { createReducer, createAction };
};

const duplicateSlice = (service, actionNamespace) => {
    const property     = service.resource;
    const listProperty = service.resources;

    const DUPLICATING_ITEM         = actionType(actionNamespace, `${property}/DUPLICATING_ITEM`);
    const DUPLICATING_ITEM_SUCCESS = actionType(actionNamespace, `${property}/DUPLICATING_ITEM_SUCCESS`);
    const DUPLICATING_ITEM_FAILURE = actionType(actionNamespace, `${property}/DUPLICATING_ITEM_FAILURE`);

    const duplicateReducer = (state, action) => {
        switch (action.type) {
            case DUPLICATING_ITEM:
                return {
                    ...state,
                    isLoadingOne: true,
                    hasLoadingError: false,
                    loadingErrorMessage: ''
                };
            case DUPLICATING_ITEM_SUCCESS:
                return {
                    ...state,
                    isLoadingOne: false,
                    hasLoadingOneError: false,
                    [`${property}SuccessfullyDuplicated`]: true,
                    [property]: action[property],
                    [listProperty]: [...(state[listProperty] || []), action[property]]
                };
            case DUPLICATING_ITEM_FAILURE:
                return {
                    ...state,
                    isLoadingOne: false,
                    hasLoadingOneError: true,
                    [`${property}SuccessfullyDuplicated`]: false,
                    loadingErrorMessage: action.error.message
                };
            default:
                return state;
        }
    }

    function duplicatingItem() { return { type: DUPLICATING_ITEM } }
    function duplicatingItemSuccess(item) { return { type: DUPLICATING_ITEM_SUCCESS, [property]: item } }
    function duplicatingItemFailure(err) { return { type: DUPLICATING_ITEM_FAILURE, error: err } }
    const duplicateAction = (item, callback) => {
        return (dispatch) => {
            dispatch(duplicatingItem());
            return service.duplicate(item)
                .then(data => {
                    const item = data[property];
                    dispatch(duplicatingItemSuccess(item));
                    callback && callback(/*err*/null, item)
                })
                .catch(err => {
                    dispatch(duplicatingItemFailure(err))
                    callback && callback(err);
                });
        }
    };

    return { duplicateReducer, duplicateAction };
};

const getSlice = (service, actionNamespace) => {
    const property     = service.resource;
    const listProperty = service.resources;
    const idField      = idFieldName(service);

    const LOADING_ITEM         = actionType(actionNamespace, `${property}/LOADING_ITEM`);
    const LOADING_ITEM_SUCCESS = actionType(actionNamespace, `${property}/LOADING_ITEM_SUCCESS`);
    const LOADING_ITEM_FAILURE = actionType(actionNamespace, `${property}/LOADING_ITEM_FAILURE`);

    const getReducer = (state, action) => {
        switch (action.type) {
            case LOADING_ITEM:
                return {
                    ...state,
                    isLoadingOne: true,
                    hasLoadingOneError: false,
                    loadingItemId: action.requestedId,
                    [`${property}SuccessfullyCreated`]: false,
                    [`${property}SuccessfullyDeleted`]: false,
                };
            case LOADING_ITEM_SUCCESS:
                if (state.loadingItemId !== action.requestedId)
                    return state;
                return {
                    ...state,
                    isLoadingOne: false,
                    hasLoadingOneError: false,
                    loadingOneErrorMessage: '',
                    loadingItemId: null,
                    [property]: action[property],
                    [listProperty]: (state[listProperty] || [])?.map(p => {
                        if (p[idField] === action[property][idField])
                            return action[property];
                        return p;
                    })
                };
            case LOADING_ITEM_FAILURE:
                if (state.loadingItemId !== action.requestedId)
                    return state;
                return {
                    ...state,
                    isLoadingOne: false,
                    hasLoadingOneError: true,
                    loadingItemId: null,
                    loadingOneErrorMessage: action.error.message
                };
            default:
                return state;
        }
    };

    function loading(requestedId) { return { type: LOADING_ITEM, requestedId } }
    function loadingSuccess(item, requestedId) { return { type: LOADING_ITEM_SUCCESS, requestedId, [property]: item } }
    function loadingFailure(err, requestedId) { return { type: LOADING_ITEM_FAILURE, requestedId, error: err } }

    const getAction = (id, callback) => {
        return (dispatch) => {
            dispatch(loading(id));
            return service.get(id)
                .then(data => {
                    const item = data[property];
                    dispatch(loadingSuccess(item, id));
                    callback && callback(/*err*/null, item)
                })
                .catch(err => {
                    dispatch(loadingFailure(err, id))
                    callback && callback(err);
                });
        }
    };

    return { getReducer, getAction };
};

const listSlice = (service, actionNamespace) => {
    const property = service.resources;
    const singleProperty = service.resource;
    const idField = idFieldName(service);

    const LOADING_ITEMS         = actionType(actionNamespace, `${property}/LOADING_ITEMS`);
    const LOADING_ITEMS_SUCCESS = actionType(actionNamespace, `${property}/LOADING_ITEMS_SUCCESS`);
    const LOADING_ITEMS_FAILURE = actionType(actionNamespace, `${property}/LOADING_ITEMS_FAILURE`);

    const listReducer = (state, action) => {
        switch (action.type) {
            case LOADING_ITEMS:
                return {
                    ...state,
                    isLoading: true,
                    listFetchScope: action.fetchScope ?? 'major',
                    hasLoadingError: false,
                    loadingErrorMessage: '',
                    hasDeleteError: false,
                    deleteErrorMessage: '',
                };
            case LOADING_ITEMS_SUCCESS: {
                const listRows = action[property] || [];
                const matchedItem = !state[singleProperty] ? null : listRows.find(p => {
                    return p[idField] === state[singleProperty][idField];
                });
                const listingKeys =
                    service.model && typeof service.model.getListingFields === 'function'
                        ? service.model.getListingFields()
                        : [idField];
                const rawMerged = !state[singleProperty]
                    ? null
                    : (matchedItem
                        // LIST rows are often `new Model(partial)`; constructors may set defaults
                        // (e.g. FormLayout forces `fields: []`) that must not overwrite a full GET.
                        ? { ...state[singleProperty], ...pick(matchedItem, listingKeys) }
                        : state[singleProperty]);
                // Spread loses class methods (e.g. Host); re-wrap so detail views keep model API.
                const mergedCurrentItem =
                    matchedItem && service.model && rawMerged
                        ? new service.model(rawMerged)
                        : rawMerged;
                return {
                    ...state,
                    isLoading: false,
                    listFetchScope: 'idle',
                    hasLoadingError: false,
                    [property]: listRows,
                    [singleProperty]: mergedCurrentItem,
                    // reset creation and deletion states
                    [`${singleProperty}SuccessfullyCreated`]: false,
                    [`${singleProperty}SuccessfullyDeleted`]: false,
                };
            }
            case LOADING_ITEMS_FAILURE:
                return {
                    ...state,
                    isLoading: false,
                    listFetchScope: 'idle',
                    hasLoadingError: true,
                    loadingErrorMessage: action.error.message,
                    // Drop stale privileged rows (e.g. after spoof / lost readmany).
                    [property]: [],
                };
            default:
                return state;
        }
    };

    function loading(fetchScope) { return { type: LOADING_ITEMS, fetchScope: fetchScope ?? 'major' } }
    function loadingSuccess(items) { return { type: LOADING_ITEMS_SUCCESS, [property]: items } }
    function loadingFailure(err) { return { type: LOADING_ITEMS_FAILURE, error: err } }
    const listAction = (filters, callback, options = {}) => {
        const fetchScope = options.fetchScope ?? 'major';
        return (dispatch) => {
            dispatch(loading(fetchScope));
            return service.list(filters)
                .then(data => {
                    const items = data[property];
                    dispatch(loadingSuccess(items));
                    callback && callback(/*err*/null, items)
                })
                .catch(err => {
                    dispatch(loadingFailure(err))
                    callback && callback(err);
                });
        }
    };

    return { listReducer, listAction };
}

const linkedResourcesSlice = (service, actionNamespace) => {
    const property = service.resources;
    const LOADING_LINKED_RESOURCES         = actionType(actionNamespace, `${property}/LOADING_LINKED_RESOURCES`);
    const LOADING_LINKED_RESOURCES_SUCCESS = actionType(actionNamespace, `${property}/LOADING_LINKED_RESOURCES_SUCCESS`);
    const LOADING_LINKED_RESOURCES_FAILURE = actionType(actionNamespace, `${property}/LOADING_LINKED_RESOURCES_FAILURE`);

    const linkedResourcesReducer = (state, action) => {
        switch (action.type) {
            case LOADING_LINKED_RESOURCES:
                return {
                    ...state,
                    isLoadingLinkedResources: true,
                    hasLoadingError: false,
                    loadingErrorMessage: '',
                };
            case LOADING_LINKED_RESOURCES_SUCCESS:
                return {
                    ...state,
                    isLoadingLinkedResources: false,
                    hasLoadingError: false,
                    linkedResources: action.linkedResources
                };
            case LOADING_LINKED_RESOURCES_FAILURE:
                return {
                    ...state,
                    isLoadingLinkedResources: false,
                    hasLoadingError: true,
                    loadingErrorMessage: action.error.message
                };
            default:
                return state;
        }
    };

    function loadingLinkedResources() { return { type: LOADING_LINKED_RESOURCES } }
    function loadingLinkedResourcesSuccess(linkedResources) { return { type: LOADING_LINKED_RESOURCES_SUCCESS, linkedResources } }
    function loadingLinkedResourcesFailure(err) { return { type: LOADING_LINKED_RESOURCES_FAILURE, error: err } }
    const linkedResourcesAction = (resources, params, callback) => {
        return (dispatch) => {
            dispatch(loadingLinkedResources());
            return service.getLinkedResources(resources, params)
                .then(data => {
                    const linkedResources = data.linked_resources;
                    dispatch(loadingLinkedResourcesSuccess(linkedResources));
                    callback && callback(/*err*/null, linkedResources)
                })
                .catch(err => {
                    dispatch(loadingLinkedResourcesFailure(err))
                    callback && callback(err);
                });
        }
    };

    return { linkedResourcesReducer, linkedResourcesAction };
}

const updateSlice = (service, actionNamespace) => {
    const property     = service.resource;
    const listProperty = service.resources;
    const idField      = idFieldName(service);

    const UPDATING_ITEM         = actionType(actionNamespace, `${property}/UPDATING_ITEM`);
    const UPDATING_ITEM_SUCCESS = actionType(actionNamespace, `${property}/UPDATING_ITEM_SUCCESS`);
    const UPDATING_ITEM_FAILURE = actionType(actionNamespace, `${property}/UPDATING_ITEM_FAILURE`);

    const updateReducer = (state, action) => {
        switch (action.type) {
            case UPDATING_ITEM:
                return {
                    ...state,
                    isLoadingOne: true,
                    hasLoadingOneError: false,
                    loadingErrorMessage: ''
                };
            case UPDATING_ITEM_SUCCESS:
                return {
                    ...state,
                    isLoadingOne: false,
                    hasLoadingOneError: false,
                    [property]: action[property],
                    [listProperty]: (state[listProperty] || [])?.map(p => {
                        if (p[idField] === action[property][idField])
                            return action[property];
                        return p;
                    })
                };
            case UPDATING_ITEM_FAILURE:
                return {
                    ...state,
                    isLoadingOne: false,
                    hasLoadingOneError: true,
                    loadingErrorMessage: action.error.message
                };
            default:
                return state;
        }
    };

    function updatingItem() { return { type: UPDATING_ITEM } }
    function updatingItemSuccess(item) { return { type: UPDATING_ITEM_SUCCESS, [property]: item } }
    function updatingItemFailure(err) { return { type: UPDATING_ITEM_FAILURE, error: err } }
    const updateAction = (item, callback) => {
        return (dispatch) => {
            dispatch(updatingItem());
            return service.update(item)
                .then(data => {
                    const item = data[property];
                    dispatch(updatingItemSuccess(item));
                    callback && callback(/*err*/null, item);
                })
                .catch(err => {
                    dispatch(updatingItemFailure(err))
                    callback && callback(err);
                });
        }
    };

    return { updateReducer, updateAction };
};

const destroySlice = (service, actionNamespace) => {
    const property     = service.resource;
    const listProperty = service.resources;
    const idField      = idFieldName(service);

    const DELETING_ITEM         = actionType(actionNamespace, `${property}/DELETING_ITEM`);
    const DELETING_ITEM_SUCCESS = actionType(actionNamespace, `${property}/DELETING_ITEM_SUCCESS`);
    const DELETING_ITEM_FAILURE = actionType(actionNamespace, `${property}/DELETING_ITEM_FAILURE`);

    const deleteReducer = (state, action) => {
        switch (action.type) {
            case DELETING_ITEM:
                return {
                    ...state,
                    isDeleting: true,
                    hasDeleteError: false,
                    deleteErrorMessage: '',
                    [`${property}SuccessfullyCreated`]: false,
                    [`${property}SuccessfullyDeleted`]: false,
                };
            case DELETING_ITEM_SUCCESS:
                return {
                    ...state,
                    isDeleting: false,
                    hasDeleteError: false,
                    deleteErrorMessage: '',
                    [`${property}SuccessfullyDeleted`]: true,
                    [property]: null,
                    [listProperty]: (state[listProperty] || []).filter(p => p[idField] !== action[property])
                };
            case DELETING_ITEM_FAILURE:
                return {
                    ...state,
                    isDeleting: false,
                    hasDeleteError: true,
                    deleteErrorMessage: action.error.message,
                    [`${property}SuccessfullyDeleted`]: false,
                };
            default:
                return state;
        }
    };

    function deletingItem() { return { type: DELETING_ITEM } }
    function deletingItemSuccess(item) { return { type: DELETING_ITEM_SUCCESS, [property]: item } }
    function deletingItemFailure(err) { return { type: DELETING_ITEM_FAILURE, error: err } }
    const deleteAction = (item, callback) => {
        return (dispatch) => {
            dispatch(deletingItem());
            return service.delete(item)
                .then(() => {
                    dispatch(deletingItemSuccess(item));
                    callback && callback(/*err*/null)
                })
                .catch(err => {
                    dispatch(deletingItemFailure(err))
                    callback && callback(err);
                });
        }
    };

    return { deleteReducer, deleteAction };
};

export const createRestSlices = (RestService, actionNamespace = null) => {
    return {
        initialState,
        ...resetSlice(RestService, actionNamespace),
        ...startCreateSlice(RestService, actionNamespace),
        ...createSlice(RestService, actionNamespace),
        ...duplicateSlice(RestService, actionNamespace),
        ...getSlice(RestService, actionNamespace),
        ...listSlice(RestService, actionNamespace),
        ...updateSlice(RestService, actionNamespace),
        ...destroySlice(RestService, actionNamespace),
        ...linkedResourcesSlice(RestService, actionNamespace)
    };
};

export const applyReducers = (state, action, reducers) => {
    return reducers.reduce(
        (state, reducer) => reducer(state, action),
        state
    );
};
